<script>
/**
 * Fenêtre d'aperçu unique de l'application (montée dans App.vue).
 * S'ouvre par ouvrirApercu() (helpers/apercu.js) : PDF dans un cadre,
 * photos en grand avec navigation dans une galerie, et pour les autres
 * formats (Word, Excel…) un simple bouton de téléchargement — un navigateur
 * ne sait pas les afficher.
 *
 * Android n'affiche pas les PDF dans un cadre (page blanche) : on y propose
 * « Ouvrir » dans un onglet, que le lecteur PDF du téléphone prend en charge.
 */
import { busApercu, genreDe } from "@/helpers/apercu";

export default {
  name: "ApercuFichier",
  data() {
    return {
      visible: false,
      liste: [],
      index: 0,
      // URL objet créées ici, à libérer à la fermeture
      urlsCreees: [],
    };
  },
  computed: {
    courant() {
      return this.liste[this.index] || null;
    },
    genre() {
      return this.courant ? this.courant.genre : "autre";
    },
    android() {
      return /Android/i.test(window.navigator.userAgent || "");
    },
  },
  created() {
    busApercu.$on("ouvrir", this.ouvrir);
    window.addEventListener("keydown", this.clavier);
  },
  beforeDestroy() {
    busApercu.$off("ouvrir", this.ouvrir);
    window.removeEventListener("keydown", this.clavier);
    this.liberer();
  },
  methods: {
    preparer(fichier) {
      const nom = fichier.nom || "document";
      const genre = genreDe({ nom, type: fichier.type || (fichier.blob && fichier.blob.type), url: fichier.url });
      let url = fichier.url;
      if (!url && fichier.blob) {
        // Un PDF reçu sans type explicite ne s'afficherait pas dans le cadre
        const blob = genre === "pdf" && fichier.blob.type !== "application/pdf"
          ? new Blob([fichier.blob], { type: "application/pdf" })
          : fichier.blob;
        url = window.URL.createObjectURL(blob);
        this.urlsCreees.push(url);
      }
      return { nom, genre, url };
    },
    ouvrir(demande) {
      this.liberer();
      const fichiers = demande.liste && demande.liste.length ? demande.liste : [demande];
      this.liste = fichiers.map(this.preparer);
      this.index = Math.min(Math.max(demande.index || 0, 0), this.liste.length - 1);
      this.visible = true;
    },
    fermer() {
      this.visible = false;
    },
    liberer() {
      this.urlsCreees.forEach((url) => window.URL.revokeObjectURL(url));
      this.urlsCreees = [];
      this.liste = [];
      this.index = 0;
    },
    precedent() {
      if (this.liste.length > 1) this.index = (this.index - 1 + this.liste.length) % this.liste.length;
    },
    suivant() {
      if (this.liste.length > 1) this.index = (this.index + 1) % this.liste.length;
    },
    clavier(e) {
      if (!this.visible) return;
      if (e.key === "ArrowLeft") this.precedent();
      if (e.key === "ArrowRight") this.suivant();
    },
    ongletExterne() {
      if (this.courant) window.open(this.courant.url, "_blank", "noopener");
    },
  },
};
</script>

<template>
  <b-modal
    :visible="visible"
    size="xl"
    centered
    hide-footer
    body-class="p-0 apf-corps"
    header-class="apf-entete"
    @hidden="liberer"
    @change="(v) => { if (!v) fermer(); }"
  >
    <template #modal-header="{ close }">
      <div class="apf-titre" :title="courant ? courant.nom : ''">
        <i :class="genre === 'pdf' ? 'ri-file-pdf-line' : genre === 'image' ? 'ri-image-line' : 'ri-file-line'"></i>
        <span>{{ courant ? courant.nom : "" }}</span>
        <small v-if="liste.length > 1" class="apf-compteur">{{ index + 1 }} / {{ liste.length }}</small>
      </div>
      <div class="apf-actions">
        <button v-if="courant" type="button" class="btn btn-light btn-sm" title="Ouvrir dans un onglet" @click="ongletExterne">
          <i class="ri-external-link-line"></i>
        </button>
        <a v-if="courant" :href="courant.url" :download="courant.nom" class="btn btn-light btn-sm" title="Télécharger">
          <i class="ri-download-2-line"></i><span class="d-none d-sm-inline ml-1">Télécharger</span>
        </a>
        <button type="button" class="btn btn-light btn-sm" title="Fermer" @click="close()">
          <i class="ri-close-line"></i>
        </button>
      </div>
    </template>

    <template v-if="courant">
      <div class="apf-zone">
        <button v-if="liste.length > 1" type="button" class="apf-nav apf-nav-g" aria-label="Précédent" @click="precedent">
          <i class="ri-arrow-left-s-line"></i>
        </button>

        <img v-if="genre === 'image'" :key="courant.url" :src="courant.url" :alt="courant.nom" class="apf-image" />

        <template v-else-if="genre === 'pdf' && !android">
          <iframe :key="courant.url" :src="courant.url" class="apf-pdf" :title="courant.nom"></iframe>
        </template>

        <div v-else class="apf-vide">
          <i :class="genre === 'pdf' ? 'ri-file-pdf-line' : 'ri-file-line'"></i>
          <p v-if="genre === 'pdf'">Votre téléphone affiche les PDF dans son propre lecteur.</p>
          <p v-else>Ce type de fichier ne s'affiche pas dans le navigateur.</p>
          <div>
            <button v-if="genre === 'pdf'" type="button" class="btn btn-light mr-2" @click="ongletExterne">
              <i class="ri-external-link-line mr-1"></i> Ouvrir
            </button>
            <a :href="courant.url" :download="courant.nom" class="btn btn-light">
              <i class="ri-download-2-line mr-1"></i> Télécharger
            </a>
          </div>
        </div>

        <button v-if="liste.length > 1" type="button" class="apf-nav apf-nav-d" aria-label="Suivant" @click="suivant">
          <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </template>
  </b-modal>
</template>

<style>
.apf-entete {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
}
.apf-titre {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  font-weight: 600;
}
.apf-titre span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.apf-compteur {
  color: #8a9199;
  font-weight: 500;
  flex-shrink: 0;
}
.apf-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.apf-corps {
  background: #202124;
}
.apf-zone {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}
.apf-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
.apf-pdf {
  width: 100%;
  height: 80vh;
  border: 0;
  background: #fff;
}
.apf-vide {
  color: #e8eaed;
  text-align: center;
  padding: 48px 20px;
}
.apf-vide > i {
  font-size: 48px;
  display: block;
  margin-bottom: 10px;
}
.apf-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.85);
  font-size: 26px;
  line-height: 44px;
  z-index: 2;
}
.apf-nav-g {
  left: 12px;
}
.apf-nav-d {
  right: 12px;
}
</style>
